import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { invitationsAPI, BACKEND_BASE_URL } from '../services/api';
import { useClubSettings } from '../contexts/ClubSettingsContext';
import { useAuth } from '../contexts/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { getErrorMessage } from '../utils/errorMessage';
import { displayName } from '../utils/brand';
import type { InvitationPreview } from '../types/invitation';
import { PASSWORD_HINT, PASSWORD_MIN_LENGTH, validatePassword } from '../utils/passwordPolicy';

/**
 * A meghívó elfogadása: a meghívott beállítja a nevét és a jelszavát.
 * Az email cím és a szerepkör a meghívóból jön, ezeken nem lehet változtatni.
 */
const AcceptInvite: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { settings } = useClubSettings();
  const { applySession } = useAuth();
  usePageTitle('Meghívó');

  const [invitation, setInvitation] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const preview = await invitationsAPI.getByToken(token);
      setInvitation(preview);
      setName(preview.name ?? '');
    } catch (err) {
      setLoadError(getErrorMessage(err, 'A meghívó nem érvényes.'));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError('A két jelszó nem egyezik.');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setSubmitting(true);
    try {
      const session = await invitationsAPI.accept(token, name.trim(), password);
      applySession(session);
      navigate('/', { replace: true });
    } catch (err) {
      setError(getErrorMessage(err, 'A fiók létrehozása nem sikerült.'));
      setSubmitting(false);
    }
  };

  const clubName = displayName(settings?.clubName);

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-md">
        {settings?.crestUrl && (
          <div className="mb-4 flex justify-center">
            <img
              src={`${BACKEND_BASE_URL}${settings.crestUrl}`}
              alt=""
              className="h-20 w-20 object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        <h1 className="text-center text-3xl font-extrabold text-gray-900">{clubName}</h1>

        {loading ? (
          <div className="mt-8 flex justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
          </div>
        ) : loadError ? (
          <div className="mt-8 rounded-lg bg-white p-6 text-left shadow-sm">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-500" />
              <div>
                <p className="font-medium text-gray-900">A meghívó nem használható</p>
                <p className="mt-1 text-sm text-gray-500">{loadError}</p>
                <p className="mt-3 text-sm text-gray-500">
                  Kérj új meghívót a klub adminisztrátorától.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="mt-4 w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Vissza a bejelentkezéshez
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-8 space-y-5 rounded-lg bg-white p-6 text-left shadow-sm">
            <div className="flex items-start gap-3 rounded-lg bg-green-50 p-3">
              <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-500" />
              <div className="text-sm text-green-900">
                <p className="font-medium">
                  Meghívtak {invitation?.roleLabel.toLowerCase()} szerepkörbe.
                </p>
                {invitation?.playerName && (
                  <p className="mt-0.5 opacity-90">Kapcsolódó játékos: {invitation.playerName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Email cím</label>
              <input
                type="email"
                value={invitation?.email ?? ''}
                readOnly
                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600"
              />
              <p className="mt-1 text-xs text-gray-400">A fiók ehhez a címhez jön létre.</p>
            </div>

            <div>
              <label htmlFor="invite-name" className="mb-1 block text-sm font-medium text-gray-700">
                Felhasználó neve
              </label>
              <input
                id="invite-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                autoComplete="name"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            <div>
              <label htmlFor="invite-password" className="mb-1 block text-sm font-medium text-gray-700">
                Jelszó
              </label>
              <input
                id="invite-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={PASSWORD_MIN_LENGTH}
                autoComplete="new-password"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-400">{PASSWORD_HINT}</p>
            </div>

            <div>
              <label htmlFor="invite-password2" className="mb-1 block text-sm font-medium text-gray-700">
                Jelszó megerősítése
              </label>
              <input
                id="invite-password2"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-md bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {submitting ? 'Fiók létrehozása...' : 'Fiók létrehozása és belépés'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AcceptInvite;
